import { Component, input, output, computed, signal, AfterViewInit, ViewChild, ElementRef, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ColumnConfig, ColumnType, SortDirection, SortState, PaginationState } from './table.model';
import { IconComponent } from '../../atoms/icon/icon.component';
import { BadgeComponent } from '../../atoms/badge/badge.component';
import { ButtonComponent } from '../../atoms/button/button.component';

@Component({
  selector: 'htx-table',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent<T extends { id?: string | number }> {
  /**
   * Array of row data to display
   */
  data = input.required<T[]>();

  /**
   * Array of column configurations
   */
  columns = input.required<ColumnConfig<T>[]>();

  /**
   * Optional: unique identifier field name (defaults to 'id')
   */
  rowIdField = input<keyof T>('id' as keyof T);

  /**
   * Enable pagination
   */
  enablePagination = input<boolean>(false);

  /**
   * Page size for pagination
   */
  pageSize = input<number>(10);

  /**
   * Page size signal (internal, updated from input)
   */
  pageSizeSignal = signal<number>(10);

  /**
   * Available page size options
   */
  pageSizeOptions = input<number[]>([5, 10, 25, 50]);

  /**
   * Total number of items (for API-based pagination)
   */
  totalItems = input<number | null>(null);

  /**
   * Disable client-side filtering (for external API-based filtering)
   */
  disableClientFiltering = input<boolean>(false);

  /**
   * Output event when a row is clicked
   */
  rowClicked = output<T>();

  /**
   * Output event when sort changes
   */
  sortChanged = output<SortState | null>();

  /**
   * Output event when page changes
   */
  pageChanged = output<PaginationState>();

  /**
   * Output event when filter changes
   */
  filterChanged = output<{ columnName: string; value: string }>();

  /**
   * Sort state signal
   */
  sortState = signal<SortState | null>(null);

  /**
   * Current page index signal
   */
  currentPageIndex = signal<number>(0);

  /**
   * Table wrapper reference for scroll-to-top
   */
  @ViewChild('tableRef') tableWrapper!: ElementRef<HTMLElement> | undefined;

  /**
   * Filter values per column
   */
  filterValues = signal<{ [key: string]: string }>({});

  /**
   * Active filter column for popup
   */
  activeFilterColumn = signal<string | null>(null);

  /**
   * Filter popup position (for fixed positioning)
   */
  filterPopupPosition = signal<{ top: number; left: number } | null>(null);

  /**
   * Get active filter column config
   */
  activeFilterColumnConfig = computed(() => {
    const columnName = this.activeFilterColumn();
    if (!columnName) return null;
    return this.columns().find(c => c.name === columnName) || null;
  });

  /**
   * Computed array of column names for table structure
   */
  displayedColumns = computed(() => this.columns().map(col => col.name));

  constructor() {
    effect(() => {
      const newPageSize = this.pageSize();
      // Only update if the value actually changed to avoid unnecessary emissions
      if (this.pageSizeSignal() !== newPageSize) {
        this.pageSizeSignal.set(newPageSize);
      }
    });
  }

  /**
   * Computed filtered data
   */
  filteredData = computed(() => {
    // If client-side filtering is disabled, return data as-is (for external API filtering)
    if (this.disableClientFiltering()) {
      return this.data();
    }

    let filtered = this.data();
    const filters = this.filterValues();

    Object.keys(filters).forEach(columnName => {
      const filterValue = filters[columnName].toLowerCase();
      if (filterValue) {
        filtered = filtered.filter(row => {
          const cellValue = (row as any)[columnName];
          return cellValue?.toString().toLowerCase().includes(filterValue);
        });
      }
    });

    return filtered;
  });

  /**
   * Computed sorted data
   */
  sortedData = computed(() => {
    const data = [...this.filteredData()];
    const sort = this.sortState();

    if (sort) {
      data.sort((a, b) => {
        const aVal = (a as any)[sort.columnName];
        const bVal = (b as any)[sort.columnName];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string') {
          return sort.direction === SortDirection.ASC
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sort.direction === SortDirection.ASC
          ? aVal - bVal
          : bVal - aVal;
      });
    }

    return data;
  });

  /**
   * Computed paginated data
   */
  paginatedData = computed(() => {
    if (!this.enablePagination()) {
      return this.sortedData();
    }

    const data = this.sortedData();
    const pageSize = this.pageSizeSignal();
    const pageIndex = this.currentPageIndex();
    const start = pageIndex * pageSize;
    const end = start + pageSize;

    return data.slice(start, end);
  });

  /**
   * Computed pagination state
   */
  paginationState = computed<PaginationState>(() => ({
    pageIndex: this.currentPageIndex(),
    pageSize: this.pageSizeSignal(),
    totalItems: this.totalItems() ?? this.sortedData().length
  }));

  /**
   * Computed total pages
   */
  totalPages = computed(() => {
    if (!this.enablePagination()) return 1;
    // If totalItems is provided (API-based pagination), use it
    if (this.totalItems() !== null) {
      return Math.ceil(this.totalItems()! / this.pageSizeSignal());
    }
    // Otherwise, calculate from local data (client-side pagination)
    return Math.ceil(this.sortedData().length / this.pageSizeSignal());
  });

  /**
   * Computed check if any column is filterable
   */
  hasFilterableColumns = computed(() => {
    return this.columns().some(c => c.filterable);
  });

  /**
   * Computed page numbers array for pagination with limited display
   */
  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPageIndex();
    const maxPagesToShow = 5;

    if (total <= maxPagesToShow) {
      return Array.from({ length: total }, (_, i) => i);
    }

    const pages: number[] = [];
    let start = Math.max(0, current - Math.floor(maxPagesToShow / 2));
    const end = Math.min(total, start + maxPagesToShow);

    if (end - start < maxPagesToShow) {
      start = Math.max(0, end - maxPagesToShow);
    }

    for (let i = start; i < end; i++) {
      pages.push(i);
    }

    return pages;
  });

  /**
   * Get CSS class for a cell based on column type
   */
  getCellClass(columnType: ColumnType): string {
    switch (columnType) {
      case ColumnType.DATE:
        return 'table-cell--date';
      case ColumnType.TEMPLATE:
        return 'table-cell--template';
      default:
        return 'table-cell--plain';
    }
  }

  /**
   * Format date if column type is DATE
   */
  formatDate(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Handle row click
   */
  onRowClick(row: T): void {
    this.rowClicked.emit(row);
  }

  /**
   * Get cell content for plain text columns
   */
  getCellContent(row: T, column: ColumnConfig<T>): string | number | null {
    if (column.cellContent) {
      const content = column.cellContent(row);
      if (content instanceof Date) {
        return content.toString();
      }
      return content;
    }
    const value = (row as any)[column.name];
    return value !== undefined && value !== null ? value : '-';
  }

  /**
   * Get human-readable status label
   */
  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      pending: 'Ausstehend',
      completed: 'Genehmigt',
      archived: 'Abgelehnt'
    };
    return statusMap[status] || status;
  }

  /**
   * Get badge variant for status
   */
  getStatusBadgeVariant(status: string): 'warning' | 'success' | 'danger' {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'completed':
        return 'success';
      case 'archived':
        return 'danger';
      default:
        return 'danger';
    }
  }

  /**
   * Handle column header click for sorting
   */
  onHeaderClick(column: ColumnConfig<T>): void {
    if (!column.sortable) return;

    const currentSort = this.sortState();

    // If clicking the same column, cycle: ASC -> DESC -> null (unsorted)
    if (currentSort?.columnName === column.name) {
      if (currentSort.direction === SortDirection.ASC) {
        // ASC -> DESC
        this.sortState.set({
          columnName: column.name,
          direction: SortDirection.DESC
        });
      } else {
        // DESC -> unsorted
        this.sortState.set(null);
      }
    } else {
      // Clicking a different column, start with ASC
      this.sortState.set({
        columnName: column.name,
        direction: SortDirection.ASC
      });
    }

    this.sortChanged.emit(this.sortState());
    this.currentPageIndex.set(0);
  }

  /**
   * Handle filter input
   */
  onFilterChange(columnName: string, value: string): void {
    this.filterValues.update(filters => ({
      ...filters,
      [columnName]: value
    }));
    this.currentPageIndex.set(0);
    // Emit filter change event for external handling
    this.filterChanged.emit({ columnName, value });
  }

  /**
   * Go to next page
   */
  nextPage(): void {
    if (this.currentPageIndex() < this.totalPages() - 1) {
      this.currentPageIndex.update(i => i + 1);
      this.scrollToTable();
      this.pageChanged.emit(this.paginationState());
    }
  }

  /**
   * Go to previous page
   */
  previousPage(): void {
    if (this.currentPageIndex() > 0) {
      this.currentPageIndex.update(i => i - 1);
      this.scrollToTable();
      this.pageChanged.emit(this.paginationState());
    }
  }

  /**
   * Go to specific page
   */
  goToPage(pageIndex: number): void {
    if (pageIndex >= 0 && pageIndex < this.totalPages()) {
      this.currentPageIndex.set(pageIndex);
      this.scrollToTable();
      this.pageChanged.emit(this.paginationState());
    }
  }

  /**
   * Change page size
   */
  onPageSizeChange(newSize: number): void {
    this.pageSizeSignal.set(newSize);
    this.currentPageIndex.set(0);
    this.scrollToTable();
    this.pageChanged.emit(this.paginationState());
  }

  /**
   * Scroll to table top
   */
  scrollToTable(): void {
    if (this.tableWrapper?.nativeElement) {
      setTimeout(() => {
        this.tableWrapper?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }

  /**
   * Set table wrapper reference
   */
  setTableWrapper(element: HTMLElement): void {
    // This is handled by ViewChild, but we keep the method for compatibility
  }

  /**
   * Check if Math is available in template
   */
  Math = Math;

  /**
   * Check if sort is active on column
   */
  isSortActive(columnName: string): boolean {
    return this.sortState()?.columnName === columnName;
  }

  /**
   * Get sort direction for column
   */
  getSortDirection(columnName: string): SortDirection | null {
    return this.sortState()?.columnName === columnName
      ? this.sortState()?.direction || null
      : null;
  }

  /**
   * Toggle filter popup for a column
   */
  toggleFilterPopup(columnName: string, event: Event): void {
    event.stopPropagation();
    if (this.activeFilterColumn() === columnName) {
      this.activeFilterColumn.set(null);
      this.filterPopupPosition.set(null);
    } else {
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.filterPopupPosition.set({
        top: rect.bottom + 8,
        left: rect.right - 250 // popup width is 250px, align to right edge
      });
      this.activeFilterColumn.set(columnName);
    }
  }

  /**
   * Close filter popup
   */
  closeFilterPopup(): void {
    this.activeFilterColumn.set(null);
    this.filterPopupPosition.set(null);
  }
}
